<template>
  <div class="page-container">
    <div class="content">
      <h1><strong>Terms and Conditions for Mind Your Bite</strong></h1>
      <p>Last Updated: 7.7.2025</p>
      <h2><strong>Welcome to Mind Your Bite</strong></h2>
      <p>Welcome to Mind Your Bite, a mobile application and website provided by Get Found Lab Ltd. ("we," "us," or "our"). By accessing or using our app ("Mind Your Bite") or website, you agree to be bound by these Terms and Conditions ("Terms"). Please read them carefully. If you do not agree with these Terms, you must not use the app or website.</p>
      <h2><strong>1. About Mind Your Bite</strong></h2>
      <h3><strong>Service Overview</strong></h3>
      <p>Mind Your Bite is designed to help users make informed, healthier food choices by providing:</p>
      <ul>
        <li>Barcode scanning of food and beverage products</li>
        <li>Nutritional analysis and ingredient information</li>
        <li>Personalized recommendations based on dietary preferences and health goals</li>
        <li>Mental health-conscious guidance to support positive relationships with food</li>
        <li>Community features for sharing experiences and support</li>
        <li>Educational content about nutrition and wellness</li>
      </ul>
      <h3><strong>Information Disclaimer</strong></h3>
      <p>While we strive to provide accurate and up-to-date information, Mind Your Bite:</p>
      <ul>
        <li>Does not guarantee the accuracy, completeness, or reliability of product data</li>
        <li>Is not intended to substitute professional medical, nutritional, or dietary advice</li>
        <li>Should not be relied upon for medical conditions, allergies, or health-related concerns</li>
        <li>Provides information for educational and informational purposes only</li>
      </ul>
      <p>Always consult qualified healthcare professionals for medical, dietary, or nutritional advice, especially regarding allergies, medical conditions, or specific dietary requirements.</p>
      <h2><strong>2. Acceptance of Terms</strong></h2>
      <h3><strong>Agreement Formation</strong></h3>
      <p>By accessing or using Mind Your Bite, you:</p>
      <ul>
        <li>Agree to be bound by these Terms and our <router-link to="/privacy">Privacy Policy</router-link></li>
        <li>Represent and warrant that you have the legal capacity to enter into this agreement</li>
        <li>Acknowledge that these Terms constitute a legally binding agreement between you and us</li>
        <li>Understand that your continued use constitutes acceptance of any modifications to these Terms</li>
      </ul>
      <h3><strong>Incorporation of Other Policies</strong></h3>
      <p>These Terms incorporate by reference:</p>
      <ul>
        <li>Our <router-link to="/privacy">Privacy Policy</router-link></li>
        <li>Community Guidelines</li>
        <li>Any additional terms for specific features or services</li>
      </ul>
      <h2><strong>3. Eligibility and Account Requirements</strong></h2>
      <h3><strong>Age Requirements</strong></h3>
      <ul>
        <li>Minimum age: You must be at least 18 years old to use Mind Your Bite</li>
        <li>Parental supervision: Users between 13-17 may use the service with parental consent and supervision</li>
        <li>Children under 13: The service is not intended for children under 13</li>
      </ul>
      <h3><strong>Account Registration</strong></h3>
      <p>To access certain features, you may need to create an account. You agree to:</p>
      <ul>
        <li>Provide accurate information during registration and keep it current</li>
        <li>Maintain confidentiality of your account credentials</li>
        <li>Use your account personally and not share access with others</li>
        <li>Create only one account for yourself</li>
        <li>Notify us immediately of any unauthorized use of your account</li>
        <li>Accept responsibility for all activities under your account</li>
      </ul>
      <h2><strong>4. Acceptable Use</strong></h2>
      <h3><strong>Permitted Uses</strong></h3>
      <p>You may use Mind Your Bite to:</p>
      <ul>
        <li>Scan and analyze food products for personal use</li>
        <li>Access nutritional information and recommendations</li>
        <li>Participate in community features</li>
        <li>Track your dietary preferences and goals</li>
        <li>Access educational content</li>
      </ul>
      <h3><strong>Prohibited Conduct</strong></h3>
      <p>You agree not to:</p>
      <p><strong>Technical Violations:</strong></p>
      <ul>
        <li>Reverse engineer, decompile, or attempt to extract source code</li>
        <li>Interfere with or disrupt the service's operation</li>
        <li>Use automated systems to access the service without permission</li>
        <li>Attempt to gain unauthorized access to our systems</li>
      </ul>
      <p><strong>Content Violations:</strong></p>
      <ul>
        <li>Post false, misleading, or harmful information</li>
        <li>Share content that violates others' intellectual property rights</li>
        <li>Upload malicious code or harmful content</li>
        <li>Impersonate others or misrepresent your identity</li>
      </ul>
      <p><strong>Commercial Violations:</strong></p>
      <ul>
        <li>Use the service for unauthorized commercial purposes</li>
        <li>Resell or redistribute our content or services</li>
        <li>Extract data for commercial use without permission</li>
      </ul>
      <p><strong>Community Violations:</strong></p>
      <ul>
        <li>Harass, abuse, or harm other users</li>
        <li>Post content that is discriminatory, hateful, or offensive</li>
        <li>Share personal information of others without consent</li>
        <li>Violate our Community Guidelines</li>
      </ul>
      <h2><strong>5. Intellectual Property Rights</strong></h2>
      <h3><strong>Our Rights</strong></h3>
      <p>Mind Your Bite and all related content, including but not limited to:</p>
      <ul>
        <li>Software, algorithms, and technology,</li>
        <li>Text, graphics, logos, and design elements,</li>
        <li>Nutritional databases and analysis tools,</li>
        <li>Educational content and recommendations</li>
      </ul>
      <p>are owned by us or our licensors and protected by intellectual property laws.</p>
      <h3><strong>Your Rights</strong></h3>
      <p>Subject to these Terms, we grant you a limited, non-exclusive, non-transferable license to:</p>
      <ul>
        <li>Use the app for personal, non-commercial purposes</li>
        <li>Access and view content through the service</li>
        <li>Participate in community features</li>
      </ul>
      <h3><strong>User-Generated Content</strong></h3>
      <p>When you submit content to Mind Your Bite, you:</p>
      <ul>
        <li>Retain ownership of your original content</li>
        <li>Grant us a license to use, display, and distribute your content within the service</li>
        <li>Represent and warrant that you have the right to submit the content</li>
        <li>Agree that we may moderate, edit, or remove content at our discretion</li>
      </ul>
      <h2><strong>6. Premium Services and Payments</strong></h2>
      <h3><strong>Subscription Plans</strong></h3>
      <p>Mind Your Bite may offer premium subscription plans that provide:</p>
      <ul>
        <li>Advanced nutritional analysis</li>
        <li>Personalized meal planning</li>
        <li>Enhanced community features</li>
        <li>Priority customer support</li>
        <li>Ad-free experience</li>
      </ul>
      <h3><strong>Payment Terms</strong></h3>
      <p>For premium services:</p>
      <ul>
        <li>All fees are stated in the currency displayed at purchase</li>
        <li>Payment is due at the time of subscription or renewal</li>
        <li>Automatic renewal applies unless cancelled before renewal date</li>
        <li>Price changes may occur with advance notice</li>
        <li>Taxes may be added as required by law</li>
      </ul>
      <h3><strong>Refunds and Cancellations</strong></h3>
      <ul>
        <li>App Store purchases: Subject to the respective app store's refund policy</li>
        <li>Direct purchases: May be eligible for refund within 30 days of purchase</li>
        <li>Cancellation: You may cancel your subscription at any time</li>
        <li>Refund requests: Contact our support team for assistance</li>
      </ul>
      <h2><strong>7. Data Accuracy and Third-Party Content</strong></h2>
      <h3><strong>Product Information</strong></h3>
      <p>Our product database includes information from various sources, including:</p>
      <ul>
        <li>Open Food Facts</li>
        <li>Edamam nutrition database</li>
        <li>User-contributed content</li>
        <li>Manufacturer data</li>
      </ul>
      <h3><strong>Accuracy Disclaimer</strong></h3>
      <p>We cannot guarantee that product information is:</p>
      <ul>
        <li><strong>Accurate or current:</strong> Product formulations may change</li>
        <li><strong>Complete:</strong> Some products may have incomplete data</li>
        <li><strong>Error-free:</strong> Data entry errors may occur</li>
        <li><strong>Consistent:</strong> Information may vary between sources</li>
      </ul>
      <h3><strong>User Responsibility</strong></h3>
      <p>You are responsible for:</p>
      <ul>
        <li><strong>Verifying</strong> product information by checking product labels</li>
        <li><strong>Consulting</strong> healthcare professionals for medical or dietary concerns</li>
        <li>Making <strong>informed decisions</strong> based on multiple sources of information</li>
        <li><strong>Reporting errors</strong> to help improve our database</li>
      </ul>
      <h3><strong>Third-Party Links</strong></h3>
      <p>Our service may contain links to third-party websites or services. We:</p>
      <ul>
        <li>Do not endorse third-party content or services</li>
        <li>Are not responsible for third-party practices or content</li>
        <li>Recommend reviewing third-party terms and privacy policies</li>
      </ul>
      <h2><strong>8. Health and Safety Disclaimers</strong></h2>
      <h3><strong>Not Medical Advice</strong></h3>
      <p>Mind Your Bite:</p>
      <ul>
        <li>Does not provide medical advice, diagnosis, or treatment</li>
        <li>Is not a substitute for professional healthcare</li>
        <li>Should not be relied upon for medical decisions</li>
        <li>Is intended for informational purposes only</li>
      </ul>
      <h3><strong>Health-Related Disclaimers</strong></h3>
      <p>Allergies and Medical Conditions:</p>
      <ul>
        <li>Always verify ingredient information on product packaging</li>
        <li>Consult healthcare professionals for allergy management</li>
        <li>Do not rely solely on our app for allergy-related decisions</li>
      </ul>
      <p>Dietary Restrictions:</p>
      <ul>
        <li>Our recommendations are general in nature</li>
        <li>Individual dietary needs may vary</li>
        <li>Consult registered dietitians for personalized advice</li>
      </ul>
      <p>Mental Health:</p>
      <ul>
        <li>Our app promotes positive relationships with food</li>
        <li>If you have eating disorders or mental health concerns, seek professional help</li>
        <li>Our content is not a substitute for mental health treatment</li>
      </ul>
      <h3><strong>Emergency Situations</strong></h3>
      <p>In case of allergic reactions or medical emergencies:</p>
      <ul>
        <li>Seek immediate medical attention</li>
        <li>Do not rely on our app for emergency guidance</li>
        <li>Contact emergency services as appropriate</li>
      </ul>
      <h2><strong>9. Privacy and Data Protection</strong></h2>
      <h3><strong>Data Collection and Use</strong></h3>
      <p>Our collection and use of personal information is governed by our <router-link to="/privacy">Privacy Policy</router-link>, which covers:</p>
      <ul>
        <li>Information we collect</li>
        <li>How we use your data</li>
        <li>Data sharing practices</li>
        <li>Your privacy rights</li>
        <li>Security measures</li>
      </ul>
      <h3><strong>User Consent</strong></h3>
      <p>By using Mind Your Bite, you consent to:</p>
      <ul>
        <li>Collection and processing of your personal data as described in our <router-link to="/privacy">Privacy Policy</router-link></li>
        <li>Use of cookies and similar technologies</li>
        <li>Analysis of your usage patterns to improve our service</li>
      </ul>
      <h2><strong>10. Service Availability and Modifications</strong></h2>
      <h3><strong>Service Availability</strong></h3>
      <p>We strive to provide reliable service, but:</p>
      <ul>
        <li>Service may be interrupted for maintenance, updates, or technical issues</li>
        <li>We do not guarantee continuous, uninterrupted access</li>
        <li>Availability may vary by geographic location</li>
        <li>We reserve the right to modify or discontinue features</li>
      </ul>
      <h3><strong>Service Modifications</strong></h3>
      <p>We may:</p>
      <ul>
        <li>Update the app with new features or improvements</li>
        <li>Modify existing features to enhance user experience</li>
        <li>Remove features that are no longer viable</li>
        <li>Change these Terms with appropriate notice</li>
      </ul>
      <h3><strong>User Responsibility for Updates</strong></h3>
      <p>You are responsible for:</p>
      <ul>
        <li>Installing app updates to maintain functionality</li>
        <li>Reviewing updated Terms when modifications are made</li>
        <li>Ensuring device compatibility with current app versions</li>
      </ul>
      <h2><strong>11. Limitation of Liability</strong></h2>
      <h3><strong>Disclaimer of Warranties</strong></h3>
      <p>Mind Your Bite is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or implied, including but not limited to:</p>
      <ul>
        <li>Merchantability or fitness for a particular purpose</li>
        <li>Accuracy of information or data</li>
        <li>Uninterrupted or error-free operation</li>
        <li>Security or freedom from harmful components</li>
      </ul>
      <h3><strong>Limitation of Damages</strong></h3>
      <p>To the fullest extent permitted by law, we shall not be liable for:</p>
      <p><strong>Direct Damages:</strong></p>
      <ul>
        <li>Loss of data or information</li>
        <li>Service interruptions or downtime</li>
        <li>Inaccurate product information</li>
        <li>Technical malfunctions</li>
      </ul>
      <p><strong>Indirect Damages:</strong></p>
      <ul>
        <li>Personal injury or health issues</li>
        <li>Allergic reactions or adverse effects</li>
        <li>Lost profits or business opportunities</li>
        <li>Consequential or punitive damages</li>
      </ul>
      <p><strong>Health-Related Damages:</strong></p>
      <ul>
        <li>Harm from reliance on product information</li>
        <li>Adverse reactions to products</li>
        <li>Dietary or nutritional consequences</li>
        <li>Mental health impacts</li>
      </ul>
      <h3><strong>Maximum Liability</strong></h3>
      <p>Our total liability to you for any claims arising from or related to these Terms or your use of Mind Your Bite shall not exceed the amount you paid us in the 12 months preceding the claim.</p>
      <h2><strong>12. Indemnification</strong></h2>
      <p>You agree to indemnify, defend, and hold harmless Mind Your Bite, its affiliates, officers, directors, employees, and agents from and against any claims, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from or related to:</p>
      <ul>
        <li>Your use of the service</li>
        <li>Your violation of these Terms</li>
        <li>Your violation of any third-party rights</li>
        <li>Content you submit to the service</li>
        <li>Your negligent or wrongful conduct</li>
      </ul>
      <h2><strong>13. Dispute Resolution</strong></h2>
      <h3><strong>Governing Law</strong></h3>
      <p>These Terms are governed by the laws of [Jurisdiction], without regard to conflict of law principles.</p>
      <h3><strong>Dispute Resolution Process</strong></h3>
      <p><strong>Step 1: Direct Communication</strong> Before initiating formal proceedings, you agree to contact us directly to attempt to resolve any disputes.</p>
      <p><strong>Step 2: Mediation</strong> If direct communication fails, disputes may be submitted to mediation.</p>
      <p><strong>Step 3: Arbitration</strong> Any remaining disputes shall be resolved through binding arbitration, except for:</p>
      <ul>
        <li>Claims for injunctive relief</li>
        <li>Intellectual property disputes</li>
        <li>Small claims court matters</li>
      </ul>
      <h3><strong>Arbitration Terms</strong></h3>
      <ul>
        <li><strong>Individual basis:</strong> Arbitration on individual basis only, no class actions permitted.</li>
        <li><strong>Location:</strong> London, United Kingdom</li>
        <li><strong>Rules:</strong> Conducted under the Rules of the London Court of International Arbitration (LCIA)</li>
        <li><strong>Costs:</strong> Each party bears their own costs unless otherwise required by law</li>
      </ul>
      <h3><strong>Exceptions to Arbitration</strong></h3>
      <p>The following may be brought in court:</p>
      <ul>
        <li>Injunctive relief for intellectual property infringement</li>
        <li>Small claims court actions</li>
        <li>Emergency relief to prevent irreparable harm</li>
      </ul>
      <h2><strong>14. Termination</strong></h2>
      <h3><strong>Termination by You</strong></h3>
      <p>You may terminate your account at any time by:</p>
      <ul>
        <li>Deleting the app from your device</li>
        <li>Contacting our support team to close your account</li>
        <li>Following account deletion procedures in the app</li>
      </ul>
      <h3><strong>Termination by Us</strong></h3>
      <p>We may terminate or suspend your access immediately, without prior notice, for:</p>
      <ul>
        <li>Violation of these Terms</li>
        <li>Fraudulent or illegal activity</li>
        <li>Harm to other users or our service</li>
        <li>Extended periods of inactivity</li>
      </ul>
      <h3><strong>Effects of Termination</strong></h3>
      <p>Upon termination:</p>
      <ul>
        <li>Your right to use the service ceases immediately</li>
        <li>We may delete your account and data</li>
        <li>Certain provisions of these Terms survive termination</li>
        <li>You remain liable for any outstanding obligations</li>
      </ul>
      <h3><strong>Survival</strong></h3>
      <p>The following provisions survive termination:</p>
      <ul>
        <li>Intellectual property rights</li>
        <li>Limitation of liability</li>
        <li>Indemnification</li>
        <li>Dispute resolution</li>
        <li>Any accrued payment obligations</li>
      </ul>
      <h2><strong>15. Modifications to Terms</strong></h2>
      <h3><strong>Right to Modify</strong></h3>
      <p>We reserve the right to modify these Terms at any time. When we make changes:</p>
      <p><strong>Notice of Changes:</strong></p>
      <ul>
        <li>We will post updated Terms in the app and on our website</li>
        <li>We will notify you of material changes via email or app notification</li>
        <li>The "Last Updated" date will reflect the most recent changes</li>
      </ul>
      <p><strong>Acceptance of Changes:</strong></p>
      <ul>
        <li>Continued use of the service after changes constitutes acceptance</li>
        <li>If you disagree with changes, you must stop using the service</li>
        <li>Material changes may require explicit consent</li>
      </ul>
      <h3><strong>Types of Changes</strong></h3>
      <p><strong>We may modify Terms to:</strong></p>
      <ul>
        <li>Reflect changes in our service or features</li>
        <li>Comply with legal requirements</li>
        <li>Address security or safety concerns</li>
        <li>Clarify existing provisions</li>
      </ul>
      <h2><strong>16. General Provisions</strong></h2>
      <h3><strong>Entire Agreement</strong></h3>
      <p>These Terms, together with our Privacy Policy and any additional terms for specific features, constitute the entire agreement between you and us regarding the service.</p>
      <h3><strong>Severability</strong></h3>
      <p>If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.</p>
      <h3><strong>Waiver</strong></h3>
      <p>Our failure to enforce any provision of these Terms does not constitute a waiver of that provision or any other provision.</p>
      <h3><strong>Assignment</strong></h3>
      <p>You may not assign or transfer your rights under these Terms without our written consent. We may assign our rights and obligations without restriction.</p>
      <h3><strong>Force Majeure</strong></h3>
      <p>We are not liable for any failure or delay in performance due to circumstances beyond our reasonable control, including natural disasters, war, terrorism, or government actions.</p>
      <h3><strong>Language</strong></h3>
      <p>These Terms are written in English. Any translations are provided for convenience only, and the English version controls in case of conflicts.</p>
      <h2><strong>17. Contact Information</strong></h2>
      <p>If you have questions about these Terms or need to contact us regarding the service:</p>
      <p>Email: hello@getfoundlab.com</p>
      <p>For legal notices or formal communications, please use the legal email address or postal address provided above.</p>
      <h2><strong>18. Effective Date</strong></h2>
      <p>These Terms are effective as of 7.7.2025 and apply to all users of Mind Your Bite from that date forward.</p>
      <p>These Terms and Conditions are designed to be comprehensive and compliant with applicable laws. They should be reviewed by legal counsel before implementation and customized based on your specific business model, jurisdiction, and regulatory requirements.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TermsPage'
}
</script>

<style scoped>
.page-container {
  max-width: 800px;
  margin: 40px auto;
  padding: 0 20px;
  min-height: 100vh;
  background-color: white;
}
.content {
  line-height: 1.8;
  font-size: 16px;
  color: #333;
}
.content h1 {
  font-size: 36px;
  margin-bottom: 20px;
}
.content h2 {
  font-size: 28px;
  margin-top: 40px;
  margin-bottom: 20px;
  border-bottom: 2px solid #eaeaea;
  padding-bottom: 10px;
}
.content h3 {
  font-size: 22px;
  margin-top: 30px;
  margin-bottom: 15px;
}
.content ul {
  padding-left: 20px;
}
.content strong {
  font-weight: bold;
}
.content a {
  color: #3b82f6;
  text-decoration: none;
}
.content a:hover {
  text-decoration: underline;
}
</style>
